import { ScheduledReminderForm } from './ScheduledReminderForm';
import type { ScheduledReminder } from '@/data/scheduledReminders';
import type { ScheduledFrequency } from '@/data/scheduledReminders';
import { closeDialog } from '@/stores/dialog';
import {
  $scheduledReminders,
  addScheduledReminder,
  removeScheduledReminder,
} from '@/stores/scheduledReminders';
import { confirm } from '@/utils/alertUtils';
import { useStore } from '@nanostores/react';
import { useState } from 'react';

function formatFrequencyDisplay(freq: ScheduledFrequency): string {
  switch (freq.kind) {
    case 'interval':
      return `Every ${freq.every.hours ?? 0}h ${freq.every.minutes ?? 0}m`;
    case 'hourly':
      return `Hourly @ ${(freq.minutes ?? [0]).join(', ')} min`;
    case 'daily':
      return 'Daily';
    case 'weekly':
      return 'Weekly';
  }
}

export function ScheduledRemindersDialog() {
  const reminders = useStore($scheduledReminders);
  const [newReminder, setNewReminder] = useState<Omit<
    ScheduledReminder,
    'id' | 'lastRun'
  > | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingReminder, setEditingReminder] = useState<Omit<
    ScheduledReminder,
    'id' | 'lastRun'
  > | null>(null);

  const handleAddNew = () => {
    setNewReminder({
      frequency: { kind: 'hourly', minutes: [0] },
      template: {
        frequency: '',
        callSign: { from: '', to: '' },
        reminder: '',
        description: '',
        tags: [],
      },
      enabled: true,
    });
  };

  const handleSaveNew = () => {
    if (newReminder) {
      addScheduledReminder(newReminder);
      setNewReminder(null);
    }
  };

  const handleCancel = () => {
    setNewReminder(null);
    setEditingId(null);
    setEditingReminder(null);
  };

  const handleDelete = async (id: string) => {
    if (
      await confirm({
        title: 'Delete Scheduled Reminder',
        message: 'Delete this scheduled reminder?',
      })
    ) {
      removeScheduledReminder(id);
    }
  };

  const handleStartEdit = (reminder: ScheduledReminder) => {
    setEditingId(reminder.id);
    setEditingReminder({
      frequency: reminder.frequency,
      template: reminder.template,
      enabled: reminder.enabled,
    });
  };

  const handleSaveEdit = () => {
    if (!editingId || !editingReminder) return;

    const existingReminder = reminders.find(
      reminder => reminder.id === editingId,
    );
    if (!existingReminder) return;

    const updatedReminder: ScheduledReminder = {
      ...existingReminder,
      ...editingReminder,
    };

    const updatedReminders = reminders.map(reminder =>
      reminder.id === editingId ? updatedReminder : reminder,
    );

    $scheduledReminders.set(updatedReminders);
    setEditingId(null);
    setEditingReminder(null);
  };

  return (
    <div
      className="stop-scrolling fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={closeDialog}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-2xl font-semibold text-gray-900">
            Manage Scheduled Reminders
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Create reminders that trigger automatically at specified times
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* List of reminders */}
          <div className="space-y-4">
            {reminders.length === 0 && !newReminder && (
              <p className="text-gray-500 italic">No scheduled reminders yet</p>
            )}
            {reminders.map(reminder => (
              <div
                key={reminder.id}
                className="rounded-lg border border-gray-300 p-4"
              >
                {editingId === reminder.id && editingReminder ? (
                  <ScheduledReminderForm
                    title="Edit Scheduled Reminder"
                    submitLabel="Save Changes"
                    value={editingReminder}
                    onChange={setEditingReminder}
                    onSave={handleSaveEdit}
                    onCancel={handleCancel}
                  />
                ) : (
                  <>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {reminder.template.reminder}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {reminder.template.callSign.from}-
                          {reminder.template.callSign.to}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {formatFrequencyDisplay(reminder.frequency)}
                        </p>
                        {reminder.template.description && (
                          <p className="mt-1 text-xs text-gray-600">
                            {reminder.template.description}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStartEdit(reminder)}
                          className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(reminder.id)}
                          className="rounded bg-red-100 px-3 py-1 text-sm text-red-700 hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}

            {/* New reminder form */}
            {newReminder && (
              <ScheduledReminderForm
                title="New Scheduled Reminder"
                value={newReminder}
                onChange={setNewReminder}
                onSave={handleSaveNew}
                onCancel={handleCancel}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between border-t border-gray-200 px-6 py-4">
          {!newReminder && (
            <button
              onClick={handleAddNew}
              className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
            >
              + Add Reminder
            </button>
          )}
          <button
            onClick={closeDialog}
            className="ml-auto rounded bg-gray-300 px-4 py-2 hover:bg-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
